import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Edit, Toolbar, useNotify, useRefresh, useTranslate } from "react-admin";

import { useErrorHandler } from "../hooks/useErrorHandler";
import { useImageUploadHandler } from "../hooks/useImageUploadHandler";
import { color } from "../theme/designTokens";
import { BaseModalProps } from "../types/modal";
import { PractitionerRecord } from "../types/practitioner";
import { CloseButton, SaveButton } from "./buttons";
import { PractitionerForm, PractitionerFormData, serializePractitionerLocationScope } from "./PractitionerForm";

type PractitionerEditTransformOptions = {
  previousData?: PractitionerRecord;
};

const PractitionerEditToolbar = ({ onCancel }: { onCancel: () => void }) => {
  return (
    <Toolbar>
      <SaveButton />
      <CloseButton onClick={onCancel} />
    </Toolbar>
  );
};

const PractitionerEdit = ({ open, onClose }: BaseModalProps) => {
  const { handleImageUpload } = useImageUploadHandler<PractitionerFormData>();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleError = useErrorHandler();
  const translate = useTranslate();

  const handleTransform = (data: PractitionerFormData, options?: PractitionerEditTransformOptions) => {
    const locationIds = serializePractitionerLocationScope(data);
    if (!locationIds) {
      throw new Error(translate("practitionerLocationScope.selectedRequired"));
    }

    const previousLocationIds = options?.previousData
      ? serializePractitionerLocationScope({
          locationScope: options.previousData.locationScope,
          locationIds: options.previousData.locations.map(location => location.id),
        })
      : undefined;
    const scopeUnchanged = data.locationScope === options?.previousData?.locationScope;
    const locationsUnchanged =
      previousLocationIds !== undefined &&
      locationIds.length === previousLocationIds.length &&
      locationIds.every((locationId, index) => locationId === previousLocationIds[index]);

    const transformedData = { ...data };
    delete transformedData.locationScope;
    delete transformedData.locationIds;

    if (!scopeUnchanged || !locationsUnchanged) {
      transformedData.locationIds = locationIds;
    }

    return handleImageUpload(transformedData);
  };

  const handleSuccess = () => {
    notify("edit.practitioner.success", { type: "success" });
    refresh();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ backgroundColor: color.background }}>{translate("edit.practitioner.title")}</DialogTitle>
      <DialogContent>
        <Edit
          resource="Practitioner"
          transform={handleTransform}
          mutationMode="optimistic"
          mutationOptions={{
            onError: error => handleError(error, translate("edit.practitioner.error")),
            onSuccess: handleSuccess,
          }}
          actions={false}
        >
          <PractitionerForm toolbar={<PractitionerEditToolbar onCancel={onClose} />} />
        </Edit>
      </DialogContent>
    </Dialog>
  );
};

export default PractitionerEdit;
